import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useFormContext } from "react-hook-form";
import { useState } from "react";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import TextField from "../../../../components/Form/TextField";
import { t } from "i18next";
import {
  WRITE_INSTRUCTIONS_TO_CREATE_YOUR_CAMPAIGN,
  ADS_CAMPAIGN_LANGUAGE_PLACEHOLDER,
  ADS_CAMPAIGN_LANGUAGE_SELECT_PLACEHOLDER,
  ADS_CAMPAIGN_PLATFORMS_PLACEHOLDER,
  CREATE_A_CAMPAIGN_FOR_BRAND_NAME,
  CREATE_A_CAMPAIGN,
  TITLE_CAMPAIGN_INSTRUCTIONS,
} from "../../../../i18n/keysTranslations";
import SelectContentLanguage from "../../../../components/Form/SelectContentLanguage";
import { CONTENT_LANGUAGE_PRESET } from "../../../../utils/catalogs/contentLanguage";
import { useEnsureContentLanguageField } from "../../../../hooks/useAdvertiserContentLanguage";
import Typography from "@mui/material/Typography";
import AddPlatformsCampaign from "./AddPlatformsCampaign";
import FieldOpenMapRadius from "../../../../components/Maps/FieldOpenMapRadius";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  BOX_SHADOW_THEME,
  GRADIENT_PURPLE_FUCHSIA,
  WHITE_BORDER_BG_ICON,
  WHITE_LIGHT_BG_ICON,
} from "../../../../utils/colors";
import ChipOptional from "../../../../components/Chip/ChipOptional";
import UploadFileButton from "../../../../components/Buttons/UploadFileButton";
import DocumentFilesList from "../../../../components/Form/DocumentFilesList";
import { FILE_FORMATS } from "../../../../utils/constants";

const SetupContainer = () => {
  const {
    control,
    watch,
    setValue,
    getValues,
    brandInformation,
    formState: { errors },
  } = useFormContext();

  useEnsureContentLanguageField({
    preset: CONTENT_LANGUAGE_PRESET.ALL,
    setValue,
    getValues,
  });

  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const platforms = watch("platforms");
  const locations = watch("locations");
  const language = watch("language");

  const [attachmentsFiles, setAttachmentsFiles] = useState([]);
  const [fileNamesMapping, setFileNamesMapping] = useState({});
  const [pendingFilesCount, setPendingFilesCount] = useState(0);

  const onFileUpload = (newUrls) => {
    const urlsArray = Array.isArray(newUrls) ? newUrls : [newUrls];

    if (window.pendingFiles && window.pendingFiles.length > 0) {
      const newMapping = {};

      urlsArray.forEach((url) => {
        if (window.pendingFiles.length > 0) {
          const file = window.pendingFiles.shift();
          newMapping[url] = file.name;
        }
      });

      setFileNamesMapping((prev) => ({ ...prev, ...newMapping }));
    }

    setPendingFilesCount((prev) => Math.max(0, prev - urlsArray.length));

    setAttachmentsFiles((prevFiles) => [...prevFiles, ...urlsArray]);
    setValue("attachmentsFiles", [...attachmentsFiles, ...urlsArray]);
  };

  const onRemoveFile = (index) => {
    const removedFileUrl = attachmentsFiles[index];
    const newFiles = attachmentsFiles.filter((_, i) => i !== index);

    setFileNamesMapping((prev) => {
      const newMapping = { ...prev };
      delete newMapping[removedFileUrl];
      return newMapping;
    });

    setAttachmentsFiles(newFiles);
    setValue("attachmentsFiles", newFiles);
  };

  const onFileSelect = (files) => {
    window.pendingFiles = Array.from(files);
    setPendingFilesCount(files.length);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignSelf: "center",
        alignItems: "flex-start",
        mt: 5,
        mb: 5,
        justifyContent: "center",
        height: "100%",
        width: "100%",
        maxWidth: { xs: "100%", md: 1000 },
        overflowX: { xs: "hidden", md: "visible" },
      }}
    >
      <Grid
        container
        rowSpacing={2}
        columnSpacing={{ xs: 0, md: 2 }}
        sx={{ pb: 10, width: "100%", mx: 0 }}
      >
        {/* Brand name - Title Card */}
        <Grid item xs={12}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
            }}
          >
            {t(
              brandInformation.businessName
                ? CREATE_A_CAMPAIGN_FOR_BRAND_NAME
                : CREATE_A_CAMPAIGN,
              {
                value: brandInformation.businessName,
              }
            )}
          </Typography>
        </Grid>

        {/* Campaign Theme */}
        <Grid
          container
          item
          xs={12}
          rowSpacing={2}
          columnSpacing={{ xs: 0, sm: 2 }}
          sx={{ width: "100%", mx: 0 }}
        >
          <Grid item xs={12}>
            <Box
              sx={{
                border: "1px solid #e5e5e5",
                borderRadius: "0.95rem",
                backgroundColor: "card.background",
                height: "100%",
                width: "100%",
                display: "flex",
                p: 3,
                boxShadow: BOX_SHADOW_THEME,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    backgroundColor: WHITE_LIGHT_BG_ICON,
                    border: `1px solid ${WHITE_BORDER_BG_ICON}`,
                    borderRadius: 2,
                  }}
                >
                  <LocalOfferOutlinedIcon />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.2 }}>
                      {t(TITLE_CAMPAIGN_INSTRUCTIONS)}
                    </Typography>
                    <ChipOptional size="small" />
                  </Box>
                  <UploadFileButton
                    disableVerificationMedia={true}
                    id="upload-documents"
                    value={false}
                    name="attachedDocuments"
                    acceptedFormats={FILE_FORMATS}
                    multiple={true}
                    pathStorage={`adsCampaigns/${userID}/documents/`}
                    onChange={onFileUpload}
                    onFileSelect={onFileSelect}
                    type="icon"
                    icon={<AttachFileIcon sx={{ fontSize: "18px" }} />}
                    sx={{
                      minWidth: "auto !important",
                      width: "38px !important",
                      height: "38px !important",
                      px: "10px !important",
                    }}
                  />
                </Box>
                <TextField
                  multiline={true}
                  maxRows={4}
                  minRows={4}
                  name="context"
                  placeholder={t(WRITE_INSTRUCTIONS_TO_CREATE_YOUR_CAMPAIGN)}
                  variant="filled"
                  fullWidth
                  control={control}
                  errors={errors}
                />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <DocumentFilesList
                    files={attachmentsFiles}
                    fileNamesMapping={fileNamesMapping}
                    onRemoveFile={onRemoveFile}
                    disabled={false}
                    pendingFilesCount={pendingFilesCount}
                  />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Languages - Locations */}
          <Grid item xs={12} sm={4}>
            <Box
              sx={{
                border: "1px solid #e5e5e5",
                borderRadius: "0.95rem",
                backgroundColor: "card.background",
                height: "100%",
                width: "100%",
                display: "flex",
                p: 3,
                boxShadow: BOX_SHADOW_THEME,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    backgroundColor: WHITE_LIGHT_BG_ICON,
                    border: `1px solid ${WHITE_BORDER_BG_ICON}`,
                    borderRadius: 2,
                  }}
                >
                  <LanguageIcon />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t(ADS_CAMPAIGN_LANGUAGE_PLACEHOLDER)}
                </Typography>
                <SelectContentLanguage
                  name="language"
                  preset={CONTENT_LANGUAGE_PRESET.ALL}
                  persistOnChange
                  disableClearable={true}
                  fullWidth
                  variant="filled"
                  placeholder={t(ADS_CAMPAIGN_LANGUAGE_SELECT_PLACEHOLDER)}
                  control={control}
                  errors={errors}
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Box
              sx={{
                border: "1px solid #e5e5e5",
                borderRadius: "0.95rem",
                backgroundColor: "card.background",
                height: "100%",
                width: "100%",
                display: "flex",
                p: 3,
                boxShadow: BOX_SHADOW_THEME,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    backgroundColor: WHITE_LIGHT_BG_ICON,
                    border: `1px solid ${WHITE_BORDER_BG_ICON}`,
                    borderRadius: 2,
                  }}
                >
                  <LocationOnIcon />
                </Box>
                <FieldOpenMapRadius
                  name={"locations"}
                  campaignContext={watch("context")}
                  onCallbackSave={(locations) =>
                    setValue("locations", locations)
                  }
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            {language && locations?.locations?.length > 0 && (
              <Box
                sx={{
                  border: "1px solid #e5e5e5",
                  borderRadius: "0.95rem",
                  backgroundColor: "card.background",
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  p: 3,
                  boxShadow: BOX_SHADOW_THEME,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      backgroundColor: WHITE_LIGHT_BG_ICON,
                      border: `1px solid ${WHITE_BORDER_BG_ICON}`,
                      borderRadius: 2,
                    }}
                  >
                    <ShareOutlinedIcon
                      sx={{ color: GRADIENT_PURPLE_FUCHSIA }}
                    />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(ADS_CAMPAIGN_PLATFORMS_PLACEHOLDER)}
                  </Typography>
                  <AddPlatformsCampaign
                    className={
                      "animate__animated animate__fadeIn animate__faster"
                    }
                    label={ADS_CAMPAIGN_PLATFORMS_PLACEHOLDER}
                    filterPlatforms={platforms}
                    sxButtons={{ px: 4, height: 56, maxHeight: "unset" }}
                  />
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SetupContainer;
